<script setup>
import { computed, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  options: {
    type: Array,
    default: () => [],
    validator: options =>
      options.every(
        opt => typeof opt === 'object' && 'value' in opt && 'label' in opt
      ),
  },
  groups: {
    type: Array,
    default: () => [],
    validator: groups =>
      groups.every(
        group =>
          'label' in group &&
          Array.isArray(group.options) &&
          group.options.every(opt => 'value' in opt && 'label' in opt)
      ),
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: '',
  },
  visibleOptions: {
    type: Number,
    default: null,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
});

const modelValue = defineModel({
  type: [String, Number, Boolean],
  default: '',
});

const open = ref(false);

const useScrollableList = computed(
  () => props.visibleOptions != null && !props.groups.length
);

const selectedLabel = computed(() => {
  const selected = props.options.find(
    option => option.value === modelValue.value
  );
  if (selected) return selected.label;
  if (modelValue.value !== '' && modelValue.value != null) {
    return String(modelValue.value);
  }
  return props.placeholder;
});

const listMaxHeightClass = computed(() => {
  const rows = props.visibleOptions || 8;
  if (rows <= 4) return 'max-h-32';
  if (rows <= 6) return 'max-h-48';
  if (rows <= 8) return 'max-h-64';
  if (rows <= 10) return 'max-h-80';
  return 'max-h-96';
});

const fieldOutlineClass = computed(() => {
  if (props.disabled) {
    return 'outline-n-weak bg-n-slate-2 cursor-not-allowed opacity-60';
  }
  if (props.error) return 'outline-n-red-9 focus:outline-n-red-9';
  return 'outline-n-weak hover:outline-n-slate-6 focus:outline-n-blue-9';
});

const rootClass = computed(() =>
  props.fullWidth ? 'w-full min-w-0' : 'w-fit'
);

const toggleList = () => {
  if (props.disabled) return;
  open.value = !open.value;
};

const closeList = () => {
  open.value = false;
};

const selectOption = option => {
  if (option.disabled) return;
  modelValue.value = option.value;
  closeList();
};
</script>

<template>
  <div class="relative" :class="rootClass">
    <OnClickOutside v-if="useScrollableList" @trigger="closeList">
      <button
        type="button"
        :disabled="disabled"
        class="reset-base flex h-8 w-full items-center justify-between rounded-lg border-0 bg-n-surface-1 py-1.5 ps-3 pe-10 text-start text-sm outline-1 outline -outline-offset-1 transition-all duration-200"
        :class="fieldOutlineClass"
        :aria-expanded="open"
        aria-haspopup="listbox"
        @click="toggleList"
      >
        <span class="truncate text-n-slate-12">{{ selectedLabel }}</span>
      </button>
      <ul
        v-show="open"
        role="listbox"
        class="absolute z-50 mt-1 mb-0 w-full overflow-y-auto rounded-lg border border-n-weak bg-n-surface-1 py-1 shadow-lg"
        :class="listMaxHeightClass"
      >
        <li
          v-for="option in options"
          :key="option.value"
          role="option"
          class="flex h-8 cursor-pointer items-center px-3 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
          :class="{
            'bg-n-alpha-2 font-medium': option.value === modelValue,
            'cursor-not-allowed opacity-50': option.disabled,
          }"
          :aria-selected="option.value === modelValue"
          @mousedown.prevent.stop="selectOption(option)"
        >
          {{ option.label }}
        </li>
      </ul>
    </OnClickOutside>

    <template v-else>
      <select
        v-model="modelValue"
        :disabled="disabled"
        class="reset-base !appearance-none [-webkit-appearance:none] [-moz-appearance:none] [&::-ms-expand]:hidden bg-none rounded-lg border-0 outline-1 outline -outline-offset-1 transition-all duration-200 bg-n-surface-1 !mb-0 h-8 py-1.5 px-3 pr-10 text-sm"
        :class="[fieldOutlineClass, fullWidth ? 'w-full' : '']"
      >
        <option v-if="placeholder" value="" disabled>
          {{ placeholder }}
        </option>
        <template v-if="groups.length">
          <optgroup
            v-for="group in groups"
            :key="group.label"
            :label="group.label"
          >
            <option
              v-for="option in group.options"
              :key="option.value"
              :value="option.value"
              :disabled="option.disabled"
            >
              {{ option.label }}
            </option>
          </optgroup>
        </template>
        <template v-else>
          <option
            v-for="option in options"
            :key="option.value"
            :value="option.value"
            :disabled="option.disabled"
          >
            {{ option.label }}
          </option>
        </template>
      </select>
    </template>

    <div
      class="pointer-events-none absolute inset-y-0 right-0 z-[1] flex w-10 items-center justify-center"
    >
      <Icon
        icon="i-lucide-chevron-down"
        class="size-4 text-n-slate-11"
        :class="{ 'opacity-50': disabled }"
      />
    </div>
  </div>
</template>
